import { formatDay, formatDayTime } from "./format";
import type { ChannelInfo, LapTrace, TraceResponse } from "./types";

export const LAP_COLOURS = [
  "#38bdf8",
  "#f97316",
  "#a3e635",
  "#e879f9",
  "#facc15",
  "#fb7185",
  "#2dd4bf",
  "#c084fc",
];

export interface AlignedLap {
  lap: number;
  /** Unique across sessions, which a lap number alone is not once teams compare. */
  key: string;
  /**
   * "Lap 7" within one session; across several, as much as tells it apart — "Cyril ·
   * L7", "14 Aug · L7", "Cyril · 14 Aug · L7".
   */
  label: string;
  /** Set when the lap came from someone else's session. */
  driver: string | null;
  sessionId: string | null;
  colour: string;
  /** Elapsed lap time in seconds at each grid point. */
  time: Array<number | null>;
  /** Physical values per channel name, already dequantised. */
  channels: Record<string, Array<number | null>>;
}

export interface AlignedTrace {
  /** Distance in metres at each grid point; the shared x axis. */
  distance: Float64Array;
  laps: AlignedLap[];
  meta: Record<string, ChannelInfo>;
}

/**
 * Longest gap bridged when filling in thinned buckets, in metres.
 *
 * The ingest keeps at least one sample every few metres, so anything longer than
 * this is a hole in the recording rather than a stretch it chose to skip, and a
 * line drawn straight through it would be an invention.
 */
const MAX_FILL_M = 12;

/**
 * Track Edge is the distance to the edge on whichever side of the centre line the car
 * is, so it flips sign each time the car crosses the middle of the road. Bridging a
 * gap across that flip would draw a road a metre wide; these are only filled between
 * samples taken on the same side.
 */
const SIDED_CHANNELS = new Set(["track_edge"]);

/**
 * Puts every requested lap and channel onto one shared distance axis.
 *
 * The API returns each lap indexed by its own absolute distance bucket, and a
 * bucket with no samples is simply absent. Laps therefore do not start or end at
 * the same index, so everything is padded onto a single axis spanning the union of
 * what was returned. Doing it once here keeps the charts and the track map reading
 * from the same arrays, which is what lets a hover on one address the other.
 *
 * Buckets the ingest thinned out are filled back in here rather than left as holes.
 * The alternative is a cursor readout that blinks between a value and a dash as it
 * crosses a straight, and lap-to-lap deltas that vanish wherever the two laps
 * happened to keep different buckets.
 */
export function alignTrace(trace: TraceResponse): AlignedTrace {
  const meta: Record<string, ChannelInfo> = {};
  for (const channel of trace.channels) {
    meta[channel.name] = channel;
  }

  if (trace.laps.length === 0) {
    return { distance: new Float64Array(0), laps: [], meta };
  }

  const start = Math.min(...trace.laps.map((lap) => lap.sample_index_start));
  const end = Math.max(
    ...trace.laps.map((lap) => lap.sample_index_start + lap.lap_time_ms.length - 1),
  );
  const length = Math.max(end - start + 1, 0);

  const distance = new Float64Array(length);
  for (let i = 0; i < length; i += 1) {
    distance[i] = (start + i) * trace.distance_step_m;
  }

  const maxGap = Math.max(1, Math.round(MAX_FILL_M / trace.distance_step_m));
  const labels = lapLabels(trace.laps);

  const laps = trace.laps.map((lap, lapIndex) => {
    const offset = lap.sample_index_start - start;

    const time = new Array<number | null>(length).fill(null);
    lap.lap_time_ms.forEach((value, index) => {
      if (value !== null) time[offset + index] = value / 1000;
    });

    const channels: Record<string, Array<number | null>> = {};
    for (const [name, stored] of Object.entries(lap.values)) {
      const info = meta[name];
      const column = new Array<number | null>(length).fill(null);
      if (info) {
        stored.forEach((value, index) => {
          if (value !== null) column[offset + index] = value / info.scale + info.offset;
        });
      }
      channels[name] = fillGaps(
        column,
        info?.interpolate ?? "linear",
        maxGap,
        SIDED_CHANNELS.has(name),
      );
    }

    return {
      lap: lap.lap,
      key: lap.session_id ? `${lap.session_id}:${lap.lap}` : String(lap.lap),
      label: labels[lapIndex],
      driver: lap.driver ?? null,
      sessionId: lap.session_id ?? null,
      colour: LAP_COLOURS[lapIndex % LAP_COLOURS.length],
      // Elapsed time only ever climbs, so interpolating it is exact enough that the
      // delta traces built from it are unaffected.
      time: fillGaps(time, "linear", maxGap),
      channels,
    };
  });

  return { distance, laps, meta };
}

/**
 * What to call each lap, given everything else on the chart.
 *
 * A lap number only identifies a line within one session, and a driver's name only
 * while that driver has one session on screen. So each label carries just enough to
 * be told apart: the driver once there are several, and the session's day once a
 * driver has several sessions — with the time as well when two fall on one day.
 * Anything still identical after that is one recording uploaded twice, and is
 * numbered rather than left looking like a single line drawn in two colours.
 */
function lapLabels(laps: LapTrace[]): string[] {
  if (!laps.some((lap) => lap.session_id)) {
    return laps.map((lap) => `Lap ${lap.lap}`);
  }

  const drivers = new Set(laps.map((lap) => lap.driver ?? ""));

  const sessionsByDriver = new Map<string, Map<string, string | undefined>>();
  for (const lap of laps) {
    if (!lap.session_id) continue;
    const driver = lap.driver ?? "";
    const sessions = sessionsByDriver.get(driver) ?? new Map<string, string | undefined>();
    sessions.set(lap.session_id, lap.recorded_at);
    sessionsByDriver.set(driver, sessions);
  }

  const sessionDate = (lap: LapTrace): string | null => {
    const sessions = sessionsByDriver.get(lap.driver ?? "");
    if (!sessions || sessions.size < 2 || !lap.recorded_at) return null;
    const days = [...sessions.values()].map((iso) => (iso ? formatDay(iso) : ""));
    return new Set(days).size < days.length
      ? formatDayTime(lap.recorded_at)
      : formatDay(lap.recorded_at);
  };

  const labels = laps.map((lap) => {
    const parts: string[] = [];
    if (drivers.size > 1 && lap.driver) parts.push(lap.driver);
    const date = sessionDate(lap);
    if (date) parts.push(date);
    if (parts.length === 0) return `Lap ${lap.lap}`;
    parts.push(`L${lap.lap}`);
    return parts.join(" · ");
  });

  const seen = new Map<string, number>();
  return labels.map((label) => {
    const count = (seen.get(label) ?? 0) + 1;
    seen.set(label, count);
    return count > 1 ? `${label} (${count})` : label;
  });
}

/**
 * Fills interior gaps in place. Leading and trailing nulls are left alone: they mark
 * where the lap itself begins and ends on the shared axis, not missing detail.
 */
function fillGaps(
  values: Array<number | null>,
  mode: "linear" | "hold",
  maxGap: number,
  sameSignOnly = false,
): Array<number | null> {
  let previous = -1;

  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (value === null) continue;

    const gap = index - previous - 1;
    const from = previous >= 0 ? (values[previous] as number) : 0;
    const bridgeable = !sameSignOnly || Math.sign(from) === Math.sign(value);
    if (previous >= 0 && gap > 0 && gap <= maxGap && bridgeable) {
      for (let step = 1; step <= gap; step += 1) {
        values[previous + step] =
          mode === "hold" ? from : from + ((value - from) * step) / (gap + 1);
      }
    }
    previous = index;
  }

  return values;
}

/**
 * Time lost or gained against a reference lap at each point on the track. This is
 * the comparison that actually localises where a lap was won: a rising delta means
 * the lap is losing time through that stretch, regardless of the absolute times.
 */
export function deltaToReference(
  lap: AlignedLap,
  reference: AlignedLap,
): Array<number | null> {
  return lap.time.map((value, index) => {
    const base = reference.time[index];
    if (value === null || base === null) return null;
    return value - base;
  });
}

/**
 * How to colour the racing line.
 *
 * "pedals" says what the driver did, which is what you act on. "accel" says what the
 * car did, which reads more smoothly through technical sections where the pedals are
 * constantly moving. "laps" abandons both and gives each lap its own colour, which is
 * the only one of the three that shows where two laps took different lines.
 */
export type TrackColourMode = "pedals" | "accel" | "laps";

export const NO_DATA_COLOUR = "#52525b";

export interface ColourBand {
  colour: string;
  /** Reads as it stands, for the bands whose label is a number range. */
  label: string;
  /** Set when the label is a word, and therefore comes from the catalogue instead. */
  key?: string;
}

const PHASE_BANDS = {
  brake: { colour: "#ef4444", key: "brake", label: "Braking" },
  full: { colour: "#22c55e", key: "full", label: "Full throttle" },
  partial: { colour: "#a3e635", key: "partial", label: "Partial throttle" },
  coast: { colour: NO_DATA_COLOUR, key: "coast", label: "Coasting" },
} satisfies Record<string, ColourBand>;

export const PEDAL_LEGEND: ColourBand[] = [
  PHASE_BANDS.brake,
  PHASE_BANDS.coast,
  PHASE_BANDS.partial,
  PHASE_BANDS.full,
];

/**
 * Banded rather than a continuous ramp so that neighbouring points share a colour
 * and the line can be drawn as a few dozen strokes instead of one per sample.
 */
const ACCEL_BANDS: Array<ColourBand & { below: number }> = [
  { below: -1, colour: "#dc2626", label: "< −1 G" },
  { below: -0.5, colour: "#f87171", label: "−1 … −0.5" },
  { below: -0.1, colour: "#fca5a5", label: "−0.5 … −0.1" },
  { below: 0.1, colour: NO_DATA_COLOUR, key: "neutral", label: "neutral" },
  { below: 0.5, colour: "#bbf7d0", label: "+0.1 … +0.5" },
  { below: 1, colour: "#4ade80", label: "+0.5 … +1" },
  { below: Infinity, colour: "#16a34a", label: "> +1 G" },
];

export const ACCEL_LEGEND: ColourBand[] = ACCEL_BANDS;

/**
 * Braking wins over throttle because overlapping the two is a deliberate act
 * (trail braking, or left-foot braking to settle the car) and the brake is the more
 * informative of the two when it happens.
 */
export function pedalColour(throttle: number | null, brake: number | null): string {
  if (brake !== null && brake >= 3) return PHASE_BANDS.brake.colour;
  if (throttle === null) return PHASE_BANDS.coast.colour;
  if (throttle >= 97) return PHASE_BANDS.full.colour;
  if (throttle >= 3) return PHASE_BANDS.partial.colour;
  return PHASE_BANDS.coast.colour;
}

export function accelColour(gLongitudinal: number | null): string {
  if (gLongitudinal === null) return NO_DATA_COLOUR;
  const band = ACCEL_BANDS.find((candidate) => gLongitudinal < candidate.below);
  return band ? band.colour : NO_DATA_COLOUR;
}
